import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

// Builds the nested `subGroupBy` (a second categorical dimension) for the
// aggregateCompoundMetric query, producing a true terms x terms cross-tab.
//
// Gated to the gadgets that render a 2D cross-tab from this data (heatmap,
// sankey, matrix, and the stacked charts) so the shared pipeline is unaffected
// for every other gadget type. Only a categorical (text/boolean) dimensionB
// qualifies — time buckets (date/dayOfWeek/month) and numeric ranges continue
// to use the existing trendBy* / groupByRange mechanisms, which are mutually
// exclusive with subGroupBy on the backend.
//
// Stacked area/bar render dimensionB as the stacked series (or x-axis, per the
// altPivot toggle). Without subGroupBy the second dimension is dropped, the
// response collapses to 1D, and the metric columns get misused as categories.
//
// A matrix with a secondaryDimensionA is the exception to the trend rule: the
// backend nests groupBy (A) -> subGroupBy (secondary A) -> trend (B), giving
// rows of (A, secondary A) pairs with date columns. Without columns, it nests
// groupBy (A) -> subGroupBy (secondary A): rows of pairs, a column per metric.
const SUB_GROUP_BY_GADGETS: GadgetType[] = [
  'heatmap',
  'sankey',
  'matrix',
  'stackedArea',
  'stackedBar',
];

// Mirrors the backend cap on a trended sub group's terms size. A secondary row
// dimension's terms are capped at it, with or without a trend.
export const MAX_TRENDED_SUB_GROUP_LIMIT = 100;

const buildSecondaryDimensionSubGroupBy = (
  def: V5ChartDefinition,
): V5GroupBy | undefined => {
  const { secondaryDimensionA } = def;
  if (
    !secondaryDimensionA ||
    !secondaryDimensionA.fieldType ||
    !isSecondaryDimensionAInEffect(def)
  ) {
    return undefined;
  }

  return {
    field: secondaryDimensionA.field,
    type: secondaryDimensionA.fieldType,
    limit: Math.min(
      def.groupByLimit || MAX_TRENDED_SUB_GROUP_LIMIT,
      MAX_TRENDED_SUB_GROUP_LIMIT,
    ),
  };
};

const buildSubGroupBy = (def: V5ChartDefinition): V5GroupBy | undefined => {
  const { dimensionA, dimensionB, gadgetType } = def;

  const secondaryDimensionSubGroupBy = buildSecondaryDimensionSubGroupBy(def);
  if (secondaryDimensionSubGroupBy) {
    return secondaryDimensionSubGroupBy;
  }

  if (!SUB_GROUP_BY_GADGETS.includes(gadgetType)) {
    return undefined;
  }
  if (!dimensionA || !dimensionB) {
    return undefined;
  }
  // Mutually exclusive with any trend dimension.
  if (
    def.trendByCalendarInterval ||
    def.trendByFixedIntervalDays ||
    def.trendByFiscalInterval
  ) {
    return undefined;
  }
  // Only a categorical second dimension.
  if (dimensionB.fieldType !== 'text' && dimensionB.fieldType !== 'boolean') {
    return undefined;
  }
  if (
    dimensionB.rangeInput ||
    dimensionB.isGroupByDayOfWeek ||
    dimensionB.isGroupByMonth ||
    dimensionB.field === 'date'
  ) {
    return undefined;
  }
  // Backend constraints: sub field must differ from group field and not be id.
  if (dimensionB.field === dimensionA.field || dimensionB.field === 'id') {
    return undefined;
  }

  return {
    field: dimensionB.field,
    type: dimensionB.fieldType,
    limit: def.groupByLimit,
  };
};

export default buildSubGroupBy;
